import { useCallback, useEffect, useState } from 'react';
import { ErroDeArea } from '../../design/Estados';
import { Icone } from '../../design/Icone';
import { bN, diaCurto, iniciais, lerFotosDoDia } from '../../giro';
import type { FotoDoGiro, FotosDoGiroDoDia } from '../../giro';
import { hora } from '../../vendas';

/**
 * Operação → aba FOTOS (Carlos, 02/10): escolhe a pessoa e vê as fotos que o sistema pediu —
 * a da caixa da reposição (para finalizar quem separou) e as da prateleira (sorteio do setor).
 * Separou e não veio a foto da caixa: aviso vermelho. Só administrador do Giro chega aqui.
 */
export function FotosDoGiro({ dia }: { dia: string }) {
  const [d, setD] = useState<FotosDoGiroDoDia | null>(null);
  const [erro, setErro] = useState(false);
  const [sel, setSel] = useState<string | null>(null);
  const [aberta, setAberta] = useState<{ foto: FotoDoGiro; titulo: string } | null>(null);
  const carregar = useCallback(() => {
    setErro(false);
    void lerFotosDoDia(dia).then((r) => {
      if (r.ok) setD(r.dados);
      else setErro(true);
    });
  }, [dia]);
  useEffect(carregar, [carregar]);
  useEffect(() => {
    if (!aberta) return;
    const fechar = (e: KeyboardEvent) => e.key === 'Escape' && setAberta(null);
    window.addEventListener('keydown', fechar);
    return () => window.removeEventListener('keydown', fechar);
  }, [aberta]);

  if (erro)
    return (
      <div className="cv-corpo">
        <ErroDeArea oQue="as fotos do Giro" aoTentar={carregar} />
      </div>
    );
  if (!d)
    return (
      <div className="cv-corpo">
        <div className="cv-carregando" aria-busy="true" />
      </div>
    );
  if (!d.pessoas.length)
    return (
      <div className="cv-corpo">
        <p className="cv-cinza">Ninguém recebeu bloco do Giro em {diaCurto(dia)}.</p>
      </div>
    );
  const pessoa = d.pessoas.find((p) => p.accountId === sel) ?? d.pessoas[0];
  return (
    <div className="gr-op">
      <aside className="gr-op-setores">
        <span className="gr-rotulo">PESSOAS</span>
        {d.pessoas.map((p) => (
          <button
            key={p.accountId}
            type="button"
            className={`gr-op-setor ${pessoa?.accountId === p.accountId ? 'gr-op-setor-sel' : ''} ${p.faltas ? 'gr-op-setor-alerta' : ''}`}
            onClick={() => setSel(p.accountId)}
          >
            <span className="gr-op-setor-topo">
              <b>{p.nome}</b>
              <em>{p.fotos}</em>
            </span>
            <span className={`gr-op-setor-rod ${p.faltas ? 'gr-vermelho' : ''}`}>
              {p.faltas
                ? `faltou foto da caixa (${String(p.faltas)})`
                : `${String(p.fotos)} foto(s) · ${String(p.blocos.length)} bloco(s)`}
            </span>
          </button>
        ))}
      </aside>
      {pessoa && (
        <div className="gr-op-corpo">
          <div className="gr-op-cab">
            <span className="gr-avatar">{iniciais(pessoa.nome)}</span>
            <h1>{pessoa.nome}</h1>
            <span>
              {pessoa.fotos} foto(s) em {diaCurto(dia)}
              <em> · clique na foto para ampliar</em>
            </span>
          </div>
          {pessoa.blocos.map((b) => (
            <div key={b.missaoId} className="cv-cartao gr-fotos-bloco">
              <h2>
                {b.setor} · {bN(b.numero)}
                <span className="cv-cinza cv-peq">
                  {' '}
                  ·{' '}
                  {b.repostos
                    ? `separou ${String(b.repostos)} produto(s) para repor`
                    : 'nada para repor'}
                </span>
              </h2>
              <span className="gr-rotulo">FOTO DA CAIXA DA REPOSIÇÃO</span>
              {b.fotoDaCaixa ? (
                <div className="gr-fotos-grade">
                  <Miniatura
                    foto={b.fotoDaCaixa}
                    legenda="Caixa da reposição"
                    aoAbrir={(f) =>
                      setAberta({
                        foto: f,
                        titulo: `${pessoa.nome} · caixa da reposição · ${b.setor} ${bN(b.numero)}`,
                      })
                    }
                  />
                </div>
              ) : b.faltaFotoDaCaixa ? (
                <p className="gr-fotos-falta">
                  <Icone nome="cam" tamanho={14} traco={1.8} /> Separou produto e a foto da caixa
                  não veio
                  {b.estado === 'DONE' || b.estado === 'AUDIT_PENDING'
                    ? ''
                    : ' (bloco ainda não finalizado)'}
                  .
                </p>
              ) : (
                <p className="cv-cinza cv-peq">Não precisou — não separou nada para repor.</p>
              )}
              <span className="gr-rotulo">FOTOS DA PRATELEIRA</span>
              {b.prateleira.length ? (
                <div className="gr-fotos-grade">
                  {b.prateleira.map((f) => (
                    <Miniatura
                      key={f.id}
                      foto={f}
                      legenda={f.produto}
                      aoAbrir={(x) =>
                        setAberta({
                          foto: x,
                          titulo: `${f.produto}${f.codigo ? ` · ${f.codigo}` : ''}`,
                        })
                      }
                    />
                  ))}
                </div>
              ) : (
                <p className="cv-cinza cv-peq">Nenhuma foto de prateleira sorteada neste bloco.</p>
              )}
            </div>
          ))}
        </div>
      )}
      {aberta && (
        <div className="cv-veu gr-fotos-fundo" onClick={() => setAberta(null)}>
          <figure
            className="gr-fotos-grande"
            role="dialog"
            aria-label={aberta.titulo}
            onClick={(e) => e.stopPropagation()}
          >
            <img src={`/api/evidencias/${aberta.foto.id}`} alt={aberta.titulo} />
            <figcaption>
              <b>{aberta.titulo}</b>
              <span>
                tirada por {aberta.foto.por} às {hora(aberta.foto.em)}
              </span>
              <button type="button" className="cv-botao" onClick={() => setAberta(null)}>
                Fechar
              </button>
            </figcaption>
          </figure>
        </div>
      )}
    </div>
  );
}

function Miniatura({
  foto,
  legenda,
  aoAbrir,
}: {
  foto: FotoDoGiro;
  legenda: string;
  aoAbrir: (f: FotoDoGiro) => void;
}) {
  return (
    <button type="button" className="gr-fotos-mini" onClick={() => aoAbrir(foto)}>
      <img src={`/api/evidencias/${foto.id}`} alt={legenda} loading="lazy" />
      <b>{legenda}</b>
      <em>
        {hora(foto.em)} · {foto.por}
      </em>
    </button>
  );
}
